import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  List,
  ListItem,
  Section,
  Inline,
  BlockGrid,
  Tooltip,
} from 'chaoskit/src/components'
import { kebabCase, toLower } from 'lodash'

import { config } from '~helpers/config'
import Data from '~components/Data'
import ButtonLink from '~components/ButtonLink'
import SecurityLayout from '~layouts/SecurityLayout'
import Image from '~components/Image'

const generateSlug = (title) => kebabCase(toLower(title))

const Security = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <SecurityLayout title={pageData.title}>
      <Section>
        <Container>
          <div css={{ marginBottom: theme.space.large }}>
            <BlockGrid
              size={{ base: 2, small: 3, medium: 5 }}
              css={{
                justifyContent: 'center',
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <ListItem>
                <Tooltip content="AICPA SOC II">
                  <Image
                    style={{ height: 120 }}
                    src="https://res.cloudinary.com/gremlin/image/upload/t_default/v1600720917/Security/aicpa.svg"
                    alt="AICPA SOC II"
                  />
                </Tooltip>
              </ListItem>
              <ListItem>
                <Tooltip content="GDPR">
                  <Image
                    style={{ height: 120 }}
                    src="https://res.cloudinary.com/gremlin/image/upload/t_default/v1600720521/Security/gdpr.svg"
                    alt="GDPR"
                  />
                </Tooltip>
              </ListItem>
              <ListItem>
                <Tooltip content="OWASP">
                  <Image
                    style={{ height: 120 }}
                    src="https://res.cloudinary.com/gremlin/image/upload/t_default/v1600720587/Security/owasp.svg"
                    alt="OWASP"
                  />
                </Tooltip>
              </ListItem>
              <ListItem>
                <Tooltip content="Privacy Shield Network">
                  <Image
                    style={{ height: 120 }}
                    src="https://res.cloudinary.com/gremlin/image/upload/t_default/v1600720674/Security/privacy-shield.svg"
                    alt="Privacy Shield"
                  />
                </Tooltip>
              </ListItem>
              <ListItem>
                <Tooltip content="NIST">
                  <Image
                    src="https://res.cloudinary.com/gremlin/image/upload/t_default/v1600720736/Security/nist.svg"
                    alt="NIST"
                  />
                </Tooltip>
              </ListItem>
            </BlockGrid>
          </div>
          <div
            css={{
              fontSize: theme.fontSize.large,
              marginBottom: theme.space.xlarge,
            }}
          >
            <Data markdown>{pageData.introduction.content}</Data>

            <div css={{ marginTop: theme.space.large }}>
              <Inline css={{ justifyContent: 'center' }}>
                <ListItem>
                  <ButtonLink url={config.latestSecurityFAQ} type="primary">
                    Download Security FAQ
                  </ButtonLink>
                </ListItem>
                <ListItem>
                  <ButtonLink
                    url="/security/practices/?ref=security"
                    type="secondary"
                  >
                    See Security practices
                  </ButtonLink>
                </ListItem>
              </Inline>
            </div>
          </div>
          {pageData.faqSections && (
            <List space="large">
              {pageData.faqSections.map((section) => (
                <ListItem key={section.title}>
                  <h3 id={generateSlug(section.title)}>{section.title}</h3>
                  <Data markdown>{section.content}</Data>
                  {section.subSections && (
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.large,
                        marginTop: theme.space.large,

                        [theme.mq.medium]: {
                          gridTemplateColumns: 'repeat(2, 1fr)',
                        },
                      }}
                    >
                      {section.subSections.map((sub) => (
                        <div key={sub.title}>
                          <h5
                            id={generateSlug(sub.title)}
                            css={[
                              theme.fontSize.large__fluid,
                              { color: theme.fontColor.muted },
                            ]}
                          >
                            {sub.title}
                          </h5>
                          <Data markdown>{sub.content}</Data>
                        </div>
                      ))}
                    </div>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Container>
      </Section>
    </SecurityLayout>
  )
}

Security.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Security

export const pageQuery = graphql`
  query SecurityPageData {
    file(name: { eq: "security" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            heading
          }
          introduction {
            content
          }
          faqSections {
            title
            content
            subSections {
              title
              content
            }
          }
        }
      }
    }
  }
`
